import React, { useEffect, useState } from 'react'
import { apiUrl } from '../../../utils/endPoints/apiUrl'
import { apiRequest } from '../../../utils/config/apiRequest'
import { Save, Trash } from 'lucide-react'
import { Box, IconButton, Tooltip } from '@mui/material'
import { useFormik } from 'formik'
import NoData from '../../comman/NoData'

const colMapping = {
    paperSize: 'Paper Size',
    paperType: 'Paper Type',
    printingColor: 'Print Color',
    printSide: 'Print Side',
    additionCharge: 'Additional Charge',
}

export default function PrintPriceListing({ shopId }) {
    const [isLoading, setLoading] = useState(true)
    const [combinations, setCombinations] = useState({ data: [], headers: [] })
    const [activeRowIndex, setActiveRowIndex] = useState(null)

    const { values, setValues, handleChange } = useFormik({
        initialValues: { prices: [] },
    })

    useEffect(() => {
        if (shopId) fetchPrintingPrices()
    }, [shopId])

    const fetchPrintingPrices = async () => {
        setLoading(true)

        const { data, status } = await apiRequest({
            endUrl: apiUrl.branch.addPrintingPrice(shopId),
        })

        if (status) {
            fetchCombinations(data?.rows || [])
        } else {
            setLoading(false)
        }
    }

    const fetchCombinations = async (printingPrices = []) => {
        const { data, status } = await apiRequest({
            endUrl: apiUrl.branch.printSettingCombinations(shopId),
        })

        setLoading(false)
        if (!status) return

        const combinationsData = data?.PrintSettingCombination || []

        /** Find headers from longest object */
        const longestItem =
            combinationsData.reduce((a, b) =>
                Object.keys(a).length > Object.keys(b).length ? a : b,
                {}) || {}

        const headers = Object.keys(longestItem).map(key => ({
            key,
            name: colMapping[key],
        }))

        const prices = []

        const isSameOptional = (a, b) => (a ?? '') === (b ?? '')

        const mergedData = combinationsData.map(combination => {
            const found = printingPrices.find(p =>
                p.paperSize === combination.paperSize &&
                p.printingColor === combination.printingColor &&
                p.printSide === combination.printSide &&
                isSameOptional(p.paperType, combination.paperType) &&
                isSameOptional(p.additionCharge, combination.additionCharge)
            )

            if (found) {
                prices.push({
                    pricePerPage: found.pricePerPage || 0,
                    urgentCharge: found.urgentCharge || 0,
                })
                return found
            }

            prices.push({ pricePerPage: 0, urgentCharge: 0 })
            return combination
        })

        setCombinations({ headers, data: mergedData })
        setValues({ prices })
    }

    const handleSave = async index => {
        const row = combinations.data[index]

        const payload = {
            paperSize: row?.paperSize || '',
            printingColor: row?.printingColor || '',
            printSide: row?.printSide || '',
            paperType: row?.paperType || '',
            additionCharge: row?.additionCharge || '',
            pricePerPage: Number(values.prices[index]?.pricePerPage || 0),
            urgentCharge: Number(values.prices[index]?.urgentCharge || 0),
            ...(row?._id && { shopId }),
        }

        await apiRequest({
            method: row?._id ? 'PUT' : 'POST',
            endUrl: row?._id
                ? apiUrl.branch.editPrintingPrice(row._id)
                : apiUrl.branch.addPrintingPrice(shopId),
            body: payload,
            showMsg: true,
        })
        
        setActiveRowIndex(null)
        fetchPrintingPrices()
    }

    const handleDelete = async id => {
        if (!id) return

        await apiRequest({
            method: 'DELETE',
            endUrl: apiUrl.branch.editPrintingPrice(id),
            showMsg: true,
        })

        fetchPrintingPrices()
    }

    const handlePriceChange = (e, index) => {
        setActiveRowIndex(index)
        handleChange(e)
    }
    const isRowDisabled = index => activeRowIndex !== null && activeRowIndex !== index;

    return (
        <Box className="table-responsive bg-white">
            {isLoading && 'Loading...'}
            {combinations?.data?.length === 0 && <NoData text={' No price setup done yet.'} />}
            {!!combinations.headers.length && (
                <table
                    className="table table-striped table-bordered admintable border-0"
                    cellSpacing="0"
                    cellPadding="0"
                >
                    <thead>
                        <tr>
                            <th>#</th>
                            {combinations.headers.map(h => (
                                <th key={h.key}>{h.name}</th>
                            ))}
                            <th>Price</th>
                            <th>Urgent Price</th>
                            <th>Action</th>
                        </tr>
                    </thead>

                    <tbody>
                        {combinations.data.length ? (
                            combinations.data.map((item, index) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>

                                    {combinations.headers.map(h => (
                                        <td key={h.key}>{item?.[h.key] || '---'}</td>
                                    ))}

                                    <td>
                                        <input
                                            type="number"
                                            name={`prices[${index}].pricePerPage`}
                                            value={values.prices[index]?.pricePerPage ?? ''}
                                            onChange={(e) => handlePriceChange(e, index)}
                                            style={{ width: 70 }}
                                            disabled={isRowDisabled(index)}
                                        />
                                    </td>

                                    <td>
                                        <input
                                            type="number"
                                            name={`prices[${index}].urgentCharge`}
                                            value={values.prices[index]?.urgentCharge ?? ''}
                                            onChange={(e) => handlePriceChange(e, index)}
                                            style={{ width: 70 }}
                                            disabled={isRowDisabled(index)}
                                        />
                                    </td>

                                    <td>
                                        <Tooltip title="Save">
                                            <IconButton
                                                disabled={isRowDisabled(index)}
                                                onClick={() => handleSave(index)}>
                                                <Save size={16} />
                                            </IconButton>
                                        </Tooltip>

                                        {item?._id && (
                                            <Tooltip
                                                title="Remove">
                                                <IconButton
                                                    disabled={isRowDisabled(index)}
                                                    onClick={() => handleDelete(item._id)}>
                                                    <Trash size={16} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={9} className="text-center py-4">
                                    No price setup found.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}
        </Box>
    )
}
